import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CLAIM_DOCUMENT_REQUIREMENTS, CLAIM_TYPE_OUTLET_REQUIREMENT } from '@infoscend-claims/shared';
import type { ClaimType } from '@infoscend-claims/shared';
import { api } from '../../lib/api.js';
import { uploadDocument } from '../../lib/upload.js';
import { useDropzone } from 'react-dropzone';
import { formatCurrency } from '../../lib/formatters.js';
import { SourceInstrumentPicker } from './components/SourceInstrumentPicker.js';
import { OutletPicker } from './components/OutletPicker.js';
import { DocumentRequirementsChecklist, type EvidenceItem } from './components/DocumentRequirementsChecklist.js';
import { AIVerificationModal, type PreVerifyResult } from './components/AIVerificationModal.js';

const CLAIM_TYPES = [
  'MEMO_REDEMPTION',
  'VISIBILITY_INCENTIVE',
  'SALES_TEAM_INCENTIVE',
  'PROMO_DISCOUNT_SUBSIDY',
  'SAMPLING_ACTIVITY',
  'DAMAGE_EXPIRY',
  'OUTLET_RETURN',
  'VOLUME_REBATE',
  'NPD_LISTING',
  'COOP_MARKETING',
  'TRADE_SPEND_DRAWDOWN',
  'OTHER',
] as const;

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/heic': ['.heic'],
  'image/webp': ['.webp'],
};
const MAX_SIZE_BYTES = 20 * 1024 * 1024;

interface UploadingFile {
  name: string;
  status: 'uploading' | 'done' | 'error';
  progress: number;
  evidenceId?: string;
  error?: string;
}

interface SubmitResult {
  claim: { id: string; claimNumber: string; status: string };
}

export function NewClaimPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Form state
  const [claimType, setClaimType] = useState('');
  const [schemeId, setSchemeId] = useState('');
  const [creditMemoId, setCreditMemoId] = useState('');
  const [incentiveProgramId, setIncentiveProgramId] = useState('');
  const [outletIds, setOutletIds] = useState<string[]>([]);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [claimedAmount, setClaimedAmount] = useState('');
  const [isoCurrencyCode] = useState('IDR');
  const [notes, setNotes] = useState('');
  const [kpiRows, setKpiRows] = useState<Array<{ name: string; actual: string; target: string }>>([
    { name: '', actual: '', target: '' },
  ]);

  // Claim draft
  const [claimId, setClaimId] = useState<string | null>(null);
  const [claimNumber, setClaimNumber] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);

  // Evidence + checklist
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const pollRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const [targetSlotKey, setTargetSlotKey] = useState<string | undefined>(undefined);

  // Modal
  const [reviewing, setReviewing] = useState(false);
  const [preVerifyResult, setPreVerifyResult] = useState<PreVerifyResult | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalNotes, setModalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Material change tracking for "re-verify" banner
  const [lastVerifiedAt, setLastVerifiedAt] = useState<number | null>(null);
  const [materialChangeAfterVerify, setMaterialChangeAfterVerify] = useState(false);

  const uploadZoneRef = useRef<HTMLDivElement>(null);

  // Detect material changes after verify to show re-verify banner
  useEffect(() => {
    if (!lastVerifiedAt) return;
    setMaterialChangeAfterVerify(true);
  }, [evidence.length, outletIds[0], schemeId, creditMemoId]);

  const slots = CLAIM_DOCUMENT_REQUIREMENTS[claimType as ClaimType] ?? [];
  const requiredSlots = slots.filter((s) => s.necessity === 'REQUIRED');
  const requiredFilled = requiredSlots.every((slot) =>
    evidence.some(
      (e) =>
        e.extractionStatus === 'COMPLETED' &&
        (e.slotKey === slot.slotKey || (slot.requiredEvidenceTypes as string[]).includes(e.evidenceType)),
    ),
  );

  const outletReq = CLAIM_TYPE_OUTLET_REQUIREMENT[claimType] ?? 'optional';
  const outletValid = outletReq === 'hidden' || outletReq === 'optional' || outletIds.length === 1;

  const formValid =
    claimType.length > 0 &&
    outletValid &&
    periodStart.length > 0 &&
    periodEnd.length > 0 &&
    Number(claimedAmount) > 0;

  const canReview = formValid && requiredFilled;

  function buildCustomFieldValues(): Record<string, number> | undefined {
    if (claimType !== 'SALES_TEAM_INCENTIVE') return undefined;
    const filled = kpiRows.filter((r) => r.name.trim() && Number(r.actual) >= 0 && Number(r.target) > 0);
    if (filled.length === 0) return undefined;
    const result: Record<string, number> = {};
    for (const row of filled) {
      result[`${row.name.trim()}_actual`] = Number(row.actual);
      result[`${row.name.trim()}_target`] = Number(row.target);
    }
    return result;
  }

  // Lazy draft creation
  async function ensureDraft(): Promise<string | null> {
    if (claimId) return claimId;
    if (!claimType) return null;
    setCreatingDraft(true);
    setDraftError(null);
    try {
      const body: Record<string, unknown> = {
        claimType,
        sourceInstrumentType: creditMemoId ? 'CREDIT_MEMO' : schemeId ? 'SCHEME' : incentiveProgramId ? 'INCENTIVE_PROGRAM' : 'ADHOC',
        periodStart: periodStart || undefined,
        periodEnd: periodEnd || undefined,
        claimedAmount: Number(claimedAmount) || 0,
        isoCurrencyCode,
        notes: notes || undefined,
      };
      if (creditMemoId) body['creditMemoId'] = creditMemoId;
      if (schemeId) body['schemeId'] = schemeId;
      if (incentiveProgramId) body['incentiveProgramId'] = incentiveProgramId;
      const cfv = buildCustomFieldValues();
      if (cfv) body['customFieldValues'] = cfv;

      const r = await api.post<{ id: string; claimNumber: string }>('/claims', body);
      setClaimId(r.id);
      setClaimNumber(r.claimNumber);
      return r.id;
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : 'Failed to create claim draft');
      return null;
    } finally {
      setCreatingDraft(false);
    }
  }

  // Patch existing draft with latest form values
  async function syncDraft(id: string) {
    const cfv = buildCustomFieldValues();
    await api.patch(`/claims/${id}`, {
      periodStart: periodStart || undefined,
      periodEnd: periodEnd || undefined,
      claimedAmount: Number(claimedAmount) || 0,
      isoCurrencyCode,
      notes: notes || undefined,
      ...(outletIds.length === 1 ? { outletIds } : {}),
      ...(schemeId ? { schemeId } : {}),
      ...(creditMemoId ? { creditMemoId } : {}),
      ...(incentiveProgramId ? { incentiveProgramId } : {}),
      ...(cfv ? { customFieldValues: cfv } : {}),
    }).catch(() => {});
  }

  function pollEvidence(evidenceId: string) {
    if (pollRefs.current.has(evidenceId)) return;
    const interval = setInterval(() => {
      api.get<EvidenceItem>(`/evidence/${evidenceId}`)
        .then((ev) => {
          setEvidence((prev) => prev.map((e) => (e.id === evidenceId ? { ...e, ...ev } : e)));
          if (ev.extractionStatus === 'COMPLETED' || ev.extractionStatus === 'FAILED') {
            clearInterval(interval);
            pollRefs.current.delete(evidenceId);
          }
        })
        .catch(() => {
          clearInterval(interval);
          pollRefs.current.delete(evidenceId);
        });
    }, 2000);
    pollRefs.current.set(evidenceId, interval);
  }

  const onDrop = useCallback(
    async (accepted: File[]) => {
      if (!claimType) return;
      const id = await ensureDraft();
      if (!id) return;

      for (const file of accepted) {
        setUploadingFiles((prev) => [...prev, { name: file.name, status: 'uploading', progress: 0 }]);
        try {
          const result = await uploadDocument(file, id, (pct) => {
            setUploadingFiles((prev) => prev.map((f) => f.name === file.name ? { ...f, progress: pct } : f));
          });
          setUploadingFiles((prev) => prev.map((f) => f.name === file.name ? { ...f, status: 'done', evidenceId: result.evidenceId } : f));
          const newItem: EvidenceItem = {
            id: result.evidenceId,
            evidenceType: 'OTHER_DOCUMENT',
            fileName: file.name,
            extractionStatus: 'PENDING',
            ...(targetSlotKey ? { slotKey: targetSlotKey } : {}),
          };
          setEvidence((prev) => [...prev, newItem]);
          if (targetSlotKey) setTargetSlotKey(undefined);
          pollEvidence(result.evidenceId);
        } catch (err) {
          setUploadingFiles((prev) => prev.map((f) => f.name === file.name ? { ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' } : f));
        }
      }
    },
    [claimType, claimId, targetSlotKey, periodStart, periodEnd, claimedAmount, notes, outletIds, schemeId, creditMemoId, incentiveProgramId],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE_BYTES,
    multiple: true,
    disabled: !claimType,
  });

  function handleAssign(evidenceId: string, slotKey: string) {
    setEvidence((prev) => prev.map((e) => (e.id === evidenceId ? { ...e, slotKey } : e)));
  }

  async function handleReviewClaim() {
    if (!canReview) return;
    setReviewing(true);
    setSubmitError(null);
    setMaterialChangeAfterVerify(false);
    try {
      let id = claimId;
      if (!id) {
        id = await ensureDraft();
        if (!id) { setReviewing(false); return; }
      } else {
        await syncDraft(id);
      }
      const r = await api.post<PreVerifyResult>(`/claims/${id}/pre-verify`, {});
      setPreVerifyResult(r);
      setLastVerifiedAt(Date.now());
      setModalNotes(notes);
      setShowModal(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('wizard.errors.submitFailed'));
    } finally {
      setReviewing(false);
    }
  }

  async function handleSaveDraft() {
    const id = await ensureDraft();
    if (id) await syncDraft(id);
  }

  async function handleModalSubmit(finalNotes: string) {
    if (!claimId) return;
    setSubmitting(true);
    setSubmitError(null);
    // Patch notes before final submit
    if (finalNotes !== notes) {
      await api.patch(`/claims/${claimId}`, { notes: finalNotes || undefined }).catch(() => {});
    }
    try {
      const r = await api.post<SubmitResult>(`/claims/${claimId}/submit`, {});
      navigate(`/claims/${r.claim.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('wizard.errors.submitFailed'));
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitAnyway(justification: string, verdict: string) {
    if (!claimId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.patch(`/claims/${claimId}`, { notes: modalNotes || undefined }).catch(() => {});
      const r = await api.post<SubmitResult>(`/claims/${claimId}/submit-despite-ai-warning`, {
        justification,
        aiVerdict: verdict,
        aiReasoning: preVerifyResult?.reasoning,
      });
      navigate(`/claims/${r.claim.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('wizard.errors.submitFailed'));
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  }

  function handleAdjustToAmount(amount: number) {
    setClaimedAmount(String(amount));
    setShowModal(false);
  }

  function handleAddMoreEvidence() {
    setShowModal(false);
    uploadZoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const missingItems: string[] = [];
  if (!claimType) missingItems.push(t('wizard.labels.claimType'));
  if (outletReq === 'required' && outletIds.length !== 1) missingItems.push(t('wizard.labels.outlets'));
  if (!periodStart || !periodEnd) missingItems.push(t('wizard.labels.period'));
  if (Number(claimedAmount) <= 0) missingItems.push(t('wizard.labels.claimedAmount'));
  if (!requiredFilled && requiredSlots.length > 0) missingItems.push(t('wizard.labels.checklist'));

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-16">
      {/* Page header */}
      <div>
        <h1 className="font-serif italic text-3xl text-ink">{t('wizard.actions.newClaim')}</h1>
        {claimNumber && (
          <p className="text-xs font-mono text-slate-400 mt-1">{claimNumber}</p>
        )}
      </div>

      {/* ── Section A: Claim details ───────────────────────────────────── */}
      <div className="bg-bone-soft border border-hairline rounded p-6 space-y-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-copper">
          {t('wizard.sections.claimDetails')}
        </p>

        {/* Claim Type + optional Source Instrument — stacked when instrument needed */}
        {(() => {
          const NO_INSTRUMENT_TYPES = ['OUTLET_RETURN', 'DAMAGE_EXPIRY', 'PROMO_DISCOUNT_SUBSIDY', 'VOLUME_REBATE', 'TRADE_SPEND_DRAWDOWN', 'OTHER'];
          const needsInstrument = claimType.length > 0 && !NO_INSTRUMENT_TYPES.includes(claimType);
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Claim Type — spans both columns when no instrument needed */}
              <div className={claimType && !needsInstrument ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  {t('wizard.labels.claimType')}
                </label>
                <select
                  value={claimType}
                  onChange={(e) => {
                    setClaimType(e.target.value);
                    setSchemeId('');
                    setCreditMemoId('');
                    setIncentiveProgramId('');
                  }}
                  className="w-full border border-hairline rounded px-3 py-2 text-sm bg-bone-soft focus:outline-none focus:border-copper"
                >
                  <option value="">{t('wizard.labels.selectClaimType')}</option>
                  {CLAIM_TYPES.map((ct) => (
                    <option key={ct} value={ct}>
                      {t(`wizard.claimTypes.${ct}.label`, { defaultValue: ct })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Source Instrument Picker — only when instrument is needed */}
              {needsInstrument && (
                <div>
                  <SourceInstrumentPicker
                    claimType={claimType}
                    value={schemeId || creditMemoId || incentiveProgramId}
                    onChange={(id, item) => {
                      setSchemeId('');
                      setCreditMemoId('');
                      setIncentiveProgramId('');
                      if (!item) return;
                      if (item.memoNumber) setCreditMemoId(id);
                      else if (item.programCode) setIncentiveProgramId(id);
                      else setSchemeId(id);
                      if (item.validFrom && !periodStart) setPeriodStart(item.validFrom.slice(0, 10));
                      if (item.validTo && !periodEnd) setPeriodEnd(item.validTo.slice(0, 10));
                    }}
                  />
                </div>
              )}
            </div>
          );
        })()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Outlet — hidden for claim types that don't require an outlet */}
          {outletReq !== 'hidden' && (
            <div className="md:col-span-2">
              <OutletPicker selected={outletIds} onChange={setOutletIds} />
              {outletReq === 'optional' && (
                <p className="text-xs text-slate-400 mt-1">{t('wizard.labels.outletOptionalHint')}</p>
              )}
            </div>
          )}

          {/* Period From / To */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              {t('wizard.labels.periodStart')}
            </label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-full border border-hairline rounded px-3 py-2 text-sm bg-bone-soft focus:outline-none focus:border-copper"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              {t('wizard.labels.periodEnd')}
            </label>
            <input
              type="date"
              value={periodEnd}
              min={periodStart}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full border border-hairline rounded px-3 py-2 text-sm bg-bone-soft focus:outline-none focus:border-copper"
            />
          </div>

          {/* Claimed Amount */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              {t('wizard.labels.claimedAmount')}
            </label>
            <div className="flex gap-2">
              <span className="flex items-center px-3 border border-hairline rounded text-sm text-slate-500 bg-bone select-none">
                {isoCurrencyCode}
              </span>
              <input
                type="number"
                min={0}
                max={10_000_000_000}
                step={1000}
                value={claimedAmount}
                onChange={(e) => setClaimedAmount(e.target.value)}
                placeholder="0"
                className="flex-1 border border-hairline rounded px-3 py-2 text-sm bg-bone-soft focus:outline-none focus:border-copper font-mono"
              />
            </div>
            {Number(claimedAmount) > 0 && (
              <p className="text-xs text-slate-400 font-mono mt-1">
                {formatCurrency(Number(claimedAmount), isoCurrencyCode)}
              </p>
            )}
            {/* Deviation hint — shown after pre-verify when claimed deviates from scheme math */}
            {(() => {
              if (!preVerifyResult?.recommendedAmount) return null;
              const recommended = preVerifyResult.recommendedAmount;
              const claimed = Number(claimedAmount);
              if (claimed <= 0 || recommended <= 0) return null;
              const deviation = Math.abs(claimed - recommended) / recommended;
              if (deviation < 0.01) return null; // <1% — no hint needed
              const deviationPct = (deviation * 100).toFixed(0);
              const hasSupportingDoc = evidence.length > (slots.filter((s) => s.necessity === 'REQUIRED').length);
              let hintClass = 'bg-copper-soft text-copper-deep';
              let hintKey = 'wizard.hints.deviationWithDoc';
              if (deviation > 0.15) {
                hintClass = 'bg-danger/10 text-danger';
                hintKey = 'wizard.hints.deviationExceeds';
              } else if (deviation > 0.05 && !hasSupportingDoc) {
                hintKey = 'wizard.hints.deviationSuggestDoc';
              }
              return (
                <p className={['text-xs mt-1.5 px-2 py-1 rounded', hintClass].join(' ')}>
                  {t(hintKey, { pct: deviationPct, defaultValue: `Your claim is ${deviationPct}% above scheme math.` })}
                </p>
              );
            })()}
          </div>

          {/* Notes (optional) */}
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              {t('wizard.labels.notes')}
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              placeholder={t('wizard.modal.notesPlaceholder')}
              className="w-full border border-hairline rounded px-3 py-2 text-sm bg-bone-soft focus:outline-none focus:border-copper resize-none"
            />
          </div>

          {/* KPI metrics — only for SALES_TEAM_INCENTIVE */}
          {claimType === 'SALES_TEAM_INCENTIVE' && (
            <div className="md:col-span-2 space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">
                  {t('wizard.labels.kpiMetrics', { defaultValue: 'KPI Metrics' })}
                </p>
                <p className="text-xs text-slate-400">
                  {t('wizard.labels.kpiMetricsHint', { defaultValue: 'Enter actual vs. target values for each KPI metric used in the incentive scheme.' })}
                </p>
              </div>
              <table className="w-full text-xs border border-hairline rounded overflow-hidden">
                <thead>
                  <tr className="bg-bone text-slate-500 text-left">
                    <th className="px-3 py-2 font-semibold uppercase tracking-wide">{t('wizard.labels.kpiMetricName', { defaultValue: 'Metric' })}</th>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wide">{t('wizard.labels.kpiActual', { defaultValue: 'Actual' })}</th>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wide">{t('wizard.labels.kpiTarget', { defaultValue: 'Target' })}</th>
                    <th className="px-2 py-2 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {kpiRows.map((row, idx) => (
                    <tr key={idx} className="border-t border-hairline">
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={row.name}
                          onChange={(e) => setKpiRows((prev) => prev.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))}
                          placeholder={t('wizard.labels.kpiMetricPlaceholder', { defaultValue: 'e.g. Sales Volume' })}
                          className="w-full border border-hairline rounded px-2 py-1 bg-bone-soft focus:outline-none focus:border-copper text-xs"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          min={0}
                          value={row.actual}
                          onChange={(e) => setKpiRows((prev) => prev.map((r, i) => i === idx ? { ...r, actual: e.target.value } : r))}
                          placeholder="0"
                          className="w-full border border-hairline rounded px-2 py-1 bg-bone-soft focus:outline-none focus:border-copper text-xs font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          min={1}
                          value={row.target}
                          onChange={(e) => setKpiRows((prev) => prev.map((r, i) => i === idx ? { ...r, target: e.target.value } : r))}
                          placeholder="100"
                          className="w-full border border-hairline rounded px-2 py-1 bg-bone-soft focus:outline-none focus:border-copper text-xs font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {kpiRows.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setKpiRows((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-slate-400 hover:text-danger transition-colors text-base leading-none"
                            aria-label="Remove row"
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={() => setKpiRows((prev) => [...prev, { name: '', actual: '', target: '' }])}
                className="text-xs text-copper hover:text-copper-warm transition-colors"
              >
                + {t('wizard.actions.addKpiRow', { defaultValue: 'Add metric' })}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Section B: Upload ──────────────────────────────────────────── */}
      <div ref={uploadZoneRef} className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-copper mb-0.5">
            {t('wizard.sections.uploadTitle')}
          </p>
          <p className="text-xs text-slate-500">{t('wizard.sections.uploadSubtitle')}</p>
        </div>

        {!claimType && (
          <div className="border-2 border-dashed border-hairline rounded-lg p-8 text-center bg-bone">
            <p className="text-sm text-slate-400">{t('wizard.info.selectClaimTypeFirst')}</p>
          </div>
        )}

        {claimType && (
          <>
            <div
              {...getRootProps()}
              className={[
                'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                isDragActive ? 'border-copper bg-copper-soft' : 'border-hairline hover:border-copper/60 bg-bone',
              ].join(' ')}
            >
              <input {...getInputProps()} />
              <div className="space-y-2">
                <div className="text-2xl text-slate-400">↑</div>
                <p className="text-sm font-medium text-ink">
                  {creatingDraft ? t('wizard.info.saving') : isDragActive ? t('extraction.dropHere') : t('extraction.dropHereOrClick')}
                </p>
                <p className="text-xs text-slate-400 font-mono">{t('extraction.acceptedFormats')}</p>
              </div>
            </div>

            {fileRejections.length > 0 && (
              <div className="text-xs text-danger font-mono">
                {fileRejections.map(({ file, errors }) => (
                  <div key={file.name}>{file.name}: {errors.map((e) => e.message).join(', ')}</div>
                ))}
              </div>
            )}

            {(uploadingFiles.length > 0 || evidence.length > 0) && (
              <div className="space-y-1.5">
                {uploadingFiles
                  .filter((u) => u.status === 'uploading')
                  .map((u) => (
                    <div key={u.name} className="flex items-center gap-3 px-3 py-2 border border-hairline rounded bg-bone text-sm">
                      <span className="text-copper text-xs font-mono">↑</span>
                      <span className="flex-1 truncate text-slate-600">{u.name}</span>
                      <span className="text-xs font-mono text-slate-400">{u.progress}%</span>
                    </div>
                  ))}
                {evidence.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 px-3 py-2 border border-hairline rounded bg-bone text-sm">
                    <span className={
                      e.extractionStatus === 'COMPLETED' ? 'text-success text-xs' :
                      e.extractionStatus === 'FAILED' ? 'text-danger text-xs' : 'text-copper text-xs animate-pulse'
                    }>
                      {e.extractionStatus === 'COMPLETED' ? '✓' : e.extractionStatus === 'FAILED' ? '✗' : '↻'}
                    </span>
                    <span className="flex-1 truncate text-slate-700">{e.fileName}</span>
                    <span className="text-xs text-slate-400">{e.extractionStatus}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {draftError && (
          <p className="text-xs text-danger">{draftError}</p>
        )}
      </div>

      {/* ── Section C: Requirement checklist ──────────────────────────── */}
      {claimType && slots.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-copper">
            {t('wizard.sections.checklist')}
          </p>
          <p className="text-xs text-slate-500">{t('wizard.sections.checklistHint')}</p>
          <DocumentRequirementsChecklist
            slots={slots}
            evidence={evidence}
            onAssign={handleAssign}
            targetSlotKey={targetSlotKey}
            onSlotClick={(slotKey) => setTargetSlotKey(slotKey === targetSlotKey ? undefined : slotKey)}
          />
        </div>
      )}

      {/* Material change banner */}
      {materialChangeAfterVerify && (
        <div className="px-4 py-2.5 rounded border border-copper/40 bg-copper-soft text-copper-deep text-xs">
          {t('wizard.info.materialChanged')}
        </div>
      )}

      {/* ── Section D: Action bar ─────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 pt-4 border-t border-hairline">
        <button
          type="button"
          onClick={() => navigate('/claims')}
          className="text-sm text-slate-500 hover:text-copper transition-colors"
        >
          {t('wizard.actions.cancel')}
        </button>

        <div className="flex items-center gap-3">
          {submitError && (
            <p className="text-xs text-danger">{submitError}</p>
          )}
          <button
            type="button"
            onClick={() => void handleSaveDraft()}
            disabled={!claimType}
            className="px-4 py-2 border border-hairline text-slate-600 rounded text-sm hover:border-copper-warm disabled:opacity-40 transition-colors"
          >
            {t('wizard.actions.saveDraft')}
          </button>

          {canReview ? (
            <button
              type="button"
              onClick={() => void handleReviewClaim()}
              disabled={reviewing}
              className="px-6 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm disabled:opacity-40 transition-colors"
            >
              {reviewing ? t('wizard.info.verifying') : t('wizard.actions.reviewClaim')}
            </button>
          ) : (
            <div className="relative group">
              <button
                type="button"
                disabled
                className="px-6 py-2 bg-copper text-bone-soft rounded text-sm font-semibold opacity-40 cursor-not-allowed"
              >
                {t('wizard.actions.reviewClaim')}
              </button>
              {missingItems.length > 0 && (
                <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block z-10">
                  <div className="bg-ink text-bone-soft text-xs rounded px-3 py-2 whitespace-nowrap shadow-lg">
                    {t('wizard.info.missingFields')}:
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                      {missingItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Verification Modal */}
      {showModal && preVerifyResult && (
        <AIVerificationModal
          result={preVerifyResult}
          isoCurrencyCode={isoCurrencyCode}
          notes={modalNotes}
          onNotesChange={setModalNotes}
          onClose={() => setShowModal(false)}
          onSubmit={(finalNotes) => void handleModalSubmit(finalNotes)}
          onAdjustToAmount={handleAdjustToAmount}
          onAddMoreEvidence={handleAddMoreEvidence}
          onSubmitAnyway={(justification, verdict) => void handleSubmitAnyway(justification, verdict)}
          submitting={submitting}
        />
      )}
    </div>
  );
}
